import styled from "@emotion/styled";
import placeholderAvatar from "assets/images/default-avatar.svg";
import {
  AbsoluteLink,
  Avatar,
  Box,
  Button,
  FlexRow,
  IconArrow,
  RatingIcon,
  ScreenReaderText,
  Text,
} from "atoms";
import type { ArtistReview } from "../types";
import {
  ReviewAuthor,
  ReviewAvatar,
  ReviewRatingIcon,
  ReviewRatingText,
  ReviewUsername,
} from "./Review";

type ReviewCardProps = {
  review: ArtistReview;
};

function ReviewCard({ review }: ReviewCardProps) {
  const profileURL = `/artists/${review.reviewableId}#reviews`;

  return (
    <Review as="li" key={review.reviewId}>
      {/* TODO: link to review */}
      <AbsoluteLink to={profileURL}>
        <ScreenReaderText>Read review</ScreenReaderText>
      </AbsoluteLink>
      <FlexRow flexDirection="column" alignItems="stretch" gap=".5em">
        <SReviewAuthor>
          <ReviewAvatar
            src={review.isAnonymous ? undefined : review.displayAvatar}
            alt={
              review.isAnonymous
                ? ""
                : `Profile picture for ${review.displayName === undefined}`
            }
            loading="lazy"
          />

          <ReviewUsername size="sm">
            {review.isAnonymous || review.displayName === undefined
              ? "Anonymous"
              : review.displayName}
          </ReviewUsername>

          <ReviewRatingIcon>
            <RatingIcon rating={review.rating} aria-label="hidden" />
          </ReviewRatingIcon>

          <ReviewRatingText size="xxxs">
            <span>
              Gave <em>{review.rating}</em> to {review.displayProjectName}
            </span>
          </ReviewRatingText>
        </SReviewAuthor>

        <Divider />

        <FlexRow
          justifyContent="space-between"
          flexWrap="nowrap"
          alignItems="stretch"
        >
          <ReviewContent>
            <ReviewForYouIf>{review.forYouIf}</ReviewForYouIf>
            <Button as="span" size="xxs" variant="blank" aria-hidden>
              Read Review <IconArrow />
            </Button>
          </ReviewContent>
          <Avatar src={review.displayProjectAvatar ?? placeholderAvatar} />
        </FlexRow>
      </FlexRow>
    </Review>
  );
}

const Divider = styled.div(({ theme }) => ({
  height: 1,
  background: theme.colors.fg20,
  width: "100%",
}));

const Review = styled(Box)(({ theme }) => ({
  borderRadius: theme.borderRadius.md,
  display: "flex",
  justifyContent: "stretch",
  lineHeight: 1.25,
  overflow: "hidden",
  position: "relative",
  width: "100%",
  "&>*": {
    flex: 1,
  },
  "a::after": {
    content: "''",
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}));

const SReviewAuthor = styled(ReviewAuthor)({
  marginBottom: -5,
});

const ReviewContent = styled.div({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: 6,
  p: {
    marginBottom: 0,
  },
  span: {
    justifyContent: "flex-start",
  },
});

const lineHeight = 16;
const ReviewForYouIf = styled(Text)({
  flex: 1,
  fontSize: 13,
  lineHeight: `${lineHeight}px`,
  height: `${lineHeight * 3}px`,
  maxHeight: `${lineHeight * 3}px`,
  overflow: "hidden",
  position: "relative",
  "&::after": {
    content: "''",
    position: "absolute",
    right: 0,
    bottom: 0,
    height: `${lineHeight}px`,
    width: "75%",
    zIndex: 2,
    backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0) 0%, rgb(255 255 255) 95% )`,
    pointerEvents: "none",
  },
});

export { ReviewCard };
